import { organisationSchema, type AssetMedia } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'expo-router'
import { useState } from 'react'
import { Pressable, View } from 'react-native'
import { useDebouncedCallback } from 'use-debounce'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { Body, Label } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Button, ButtonLabel, LinkButton } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { trpc } from '~/lib/trpc'

type Organisation = {
  id: string
  name: string
  email_domain: string
  logo_media: AssetMedia | null
  created_at: string
}

function OrganisationRow({ organisation }: { organisation: Organisation }) {
  return (
    <Link href={`/(authenticated)/admin/organisations/${organisation.id}`} asChild>
      <Pressable className="justify-between gap-4 rounded-lg border border-neutral-200 bg-white p-4 active:bg-neutral-50">
        <View className="flex-1 gap-1">
          <Text className="font-medium text-base">{organisation.name}</Text>
          <Label className="text-sm text-secondary">{organisation.email_domain}</Label>
        </View>
      </Pressable>
    </Link>
  )
}

const PAGE_SIZE = 20

export default function OrganisationsIndex() {
  const { t } = useTranslations('admin')
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, 300)

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSetSearch(value)
  }

  const queryInput = { page, limit: PAGE_SIZE, search: search || undefined }

  const { data, isLoading, error, isFetching } = useQuery(
    trpc.admin.listOrganisations.queryOptions(queryInput)
  )

  const organisations = data?.organisations ?? []
  const totalPages = data?.totalPages ?? 1
  const totalCount = data?.totalCount ?? 0
  const currentPage = data?.page ?? page

  const canGoBack = currentPage > 1
  const canGoNext = currentPage < totalPages

  return (
    <ScreenView>
      <ContentBox className="flex-1" contentContainerClassName="gap-6" variant="transparent">
        <View className="gap-4">
          <Heading2 className="text-center">{t(($) => $.organisations.title)}</Heading2>
          <Body className="text-center text-neutral-600">
            {t(($) => $.organisations.description)}
          </Body>
        </View>

        <LinkButton href="/(authenticated)/admin/organisations/add" variant="primary" size="sm">
          <ButtonLabel>{t(($) => $.organisations.createButton)}</ButtonLabel>
        </LinkButton>

        <Input
          value={searchInput}
          onChangeText={handleSearchChange}
          placeholder={t(($) => $.organisations.searchPlaceholder)}
          variant="box"
          className="bg-white"
        />

        {isLoading && (
          <Body className="text-center text-neutral-500">{t(($) => $.organisations.loading)}</Body>
        )}

        {!!error && (
          <Body className="text-center text-red-600">{t(($) => $.organisations.loadError)}</Body>
        )}

        {!isLoading && !error && organisations.length === 0 && (
          <Body className="text-center text-neutral-500">
            {search ? t(($) => $.organisations.noResults) : t(($) => $.organisations.none)}
          </Body>
        )}

        {organisations.length > 0 && (
          <>
            <View className="gap-3">
              {organisations.map((organisation) => {
                const parsedOrganisation = organisationSchema.safeParse(organisation)

                if (!parsedOrganisation.success) {
                  return null
                }

                return (
                  <OrganisationRow key={organisation.id} organisation={parsedOrganisation.data} />
                )
              })}
            </View>

            <View className="flex-row items-center justify-between gap-4 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!canGoBack || isFetching}
                onPress={() => setPage(currentPage - 1)}>
                <ButtonLabel>{t(($) => $.organisations.previous)}</ButtonLabel>
              </Button>

              <Text className="text-sm text-neutral-600">
                {t(($) => $.organisations.page, {
                  current: currentPage,
                  total: totalPages,
                  count: totalCount,
                })}
              </Text>

              <Button
                variant="outline"
                size="sm"
                disabled={!canGoNext || isFetching}
                onPress={() => setPage(currentPage + 1)}>
                <ButtonLabel>{t(($) => $.organisations.next)}</ButtonLabel>
              </Button>
            </View>
          </>
        )}
      </ContentBox>
    </ScreenView>
  )
}
